// The admin console's translation layer (D61, D63): ICU MessageFormat catalogs in
// messages/<locale>.json, like the console's. Portuguese first: pt-BR before sign-in, then the
// staff member's own choice, saved on their profile. Customer data, identifiers and codes are
// never translated.
import { type Locale, locales } from '@apsio/i18n'
import { type UiText, UiTextProvider } from '@apsio/ui'
import { IntlMessageFormat } from 'intl-messageformat'
import { createContext, type ReactNode, useContext, useMemo } from 'react'
import en from '../../messages/en.json'
import ptBR from '../../messages/pt-BR.json'

export type MessageId = keyof typeof ptBR
export type Values = Record<string, string | number | Date>
export type { Locale }
export { locales }

/** D61: the admin console speaks Portuguese until a staff member chooses otherwise. */
export const DEFAULT_LOCALE: Locale = 'pt-BR'

const catalogs: Record<Locale, Record<MessageId, string>> = { en, 'pt-BR': ptBR }

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (locales as readonly string[]).includes(value)
}

export interface Translator {
  locale: Locale
  t: (id: MessageId, values?: Values) => string
  number: (value: number, options?: Intl.NumberFormatOptions) => string
  /**
   * A day ("14 de out."), with its year only when that is not the current one (admin-d.md
   * "Numbers": one day formatter, the current year dropped). Options that name no date part
   * (a timeZone alone) keep that rule; options that do (a month chip's "outubro de 2026", a
   * time) are used as given.
   */
  date: (value: Date | string | number, options?: Intl.DateTimeFormatOptions) => string
  /** A day and a time ("14 de out. 18:30"), the year under the same rule as date. */
  dateTime: (value: Date | string | number) => string
}

/** Options naming any part of a date or time: the caller chose the parts. */
const PARTS = [
  'dateStyle',
  'timeStyle',
  'weekday',
  'era',
  'year',
  'month',
  'day',
  'hour',
  'minute',
  'second',
] as const

export function createTranslator(locale: Locale, now: () => Date = () => new Date()): Translator {
  const cache = new Map<MessageId, IntlMessageFormat>()
  const yearOf = (d: Date, timeZone?: string) =>
    new Intl.DateTimeFormat('en', { year: 'numeric', timeZone }).format(d)
  /** The year part, only for a day outside the current year (in the same time zone). */
  const year = (d: Date, timeZone?: string): Intl.DateTimeFormatOptions =>
    yearOf(d, timeZone) === yearOf(now(), timeZone) ? {} : { year: 'numeric' }
  return {
    locale,
    t(id, values) {
      let format = cache.get(id)
      if (!format) {
        format = new IntlMessageFormat(catalogs[locale][id] ?? catalogs['pt-BR'][id] ?? id, locale)
        cache.set(id, format)
      }
      return String(format.format(values))
    },
    number: (value, options) => new Intl.NumberFormat(locale, options).format(value),
    date: (value, options = {}) => {
      const d = new Date(value)
      if (PARTS.some((part) => options[part] !== undefined))
        return new Intl.DateTimeFormat(locale, options).format(d)
      return new Intl.DateTimeFormat(locale, {
        ...options,
        day: 'numeric',
        month: 'short',
        ...year(d, options.timeZone),
      }).format(d)
    },
    dateTime: (value) => {
      const d = new Date(value)
      return new Intl.DateTimeFormat(locale, {
        day: 'numeric',
        month: 'short',
        ...year(d),
        hour: '2-digit',
        minute: '2-digit',
      }).format(d)
    },
  }
}

const I18nContext = createContext<Translator>(createTranslator(DEFAULT_LOCALE))

/** The words Console D's components (@apsio/ui) say themselves, from the admin's catalogue. */
function uiText(t: Translator['t']): UiText {
  return {
    status: {
      new: t('d.status.new'),
      reg: t('d.status.regressed'),
      on: t('d.status.ongoing'),
      res: t('d.status.resolved'),
      fire: t('d.status.firing'),
      miss: t('d.status.missing'),
      mute: t('d.status.muted'),
      ok: t('d.status.ok'),
    },
    copy: t('d.code.copy'),
    copied: t('d.code.copied'),
    copyFailed: t('d.code.copyFailed'),
  }
}

export function I18nProvider({ locale, children }: { locale: Locale; children: ReactNode }) {
  const translator = useMemo(() => createTranslator(locale), [locale])
  const text = useMemo(() => uiText(translator.t), [translator])
  return (
    <I18nContext.Provider value={translator}>
      <UiTextProvider value={text}>{children}</UiTextProvider>
    </I18nContext.Provider>
  )
}

export const useI18n = (): Translator => useContext(I18nContext)
